import { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import QuillBetterTable from 'quill-better-table';
import 'quill/dist/quill.snow.css';
import 'quill-better-table/dist/quill-better-table.css';
import { AlertTriangle, RefreshCw, Code } from 'lucide-react';

interface EditorProps {
  content: string;
  url: string;
  isParsing: boolean;
  error: string | null;
  onContentChange: (content: string) => void;
  onUrlChange: (url: string) => void;
  onParseContent: () => void;
  isEnabled: boolean;
  isInitialized?: boolean;
  setQuillInstance?: (instance: any) => void;
}

const cleanText = (html: string): string => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Remove all style and class attributes while preserving content structure
  temp.querySelectorAll('*').forEach(element => {
    element.removeAttribute('style');
    element.removeAttribute('class');
  });
  
  return temp.innerHTML;
};

const cleanStyles = (html: string): string => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  temp.querySelectorAll('*').forEach(element => {
    element.removeAttribute('style');
    element.removeAttribute('class');
  });
  
  return temp.innerHTML;
};

// Custom clean format button icon
const cleanIcon = `
<svg viewbox="0 0 18 18">
  <line class="ql-stroke" x1="5" x2="13" y1="3" y2="3"></line>
  <line class="ql-stroke" x1="6" x2="9.35" y1="12" y2="3"></line>
  <line class="ql-stroke" x1="11" x2="15" y1="11" y2="15"></line>
  <line class="ql-stroke" x1="15" x2="11" y1="11" y2="15"></line>
  <rect class="ql-fill" height="1" rx="0.5" ry="0.5" width="7" x="2" y="14"></rect>
</svg>
`;

// Clean styles icon
const cleanStylesIcon = `
<svg viewbox="0 0 18 18">
  <line class="ql-stroke" x1="3" x2="15" y1="3" y2="3"></line>
  <line class="ql-stroke" x1="3" x2="15" y1="9" y2="9"></line>
  <line class="ql-stroke" x1="3" x2="15" y1="15" y2="15"></line>
  <line class="ql-stroke" x1="3" x2="5" y1="5" y2="5" stroke-width="0.5"></line>
  <line class="ql-stroke" x1="3" x2="7" y1="11" y2="11" stroke-width="0.5"></line>
  <line class="ql-stroke" x1="3" x2="6" y1="17" y2="17" stroke-width="0.5"></line>
</svg>
`;

// Table icon
const tableIcon = `
<svg viewbox="0 0 18 18">
  <rect class="ql-stroke" height="12" width="12" x="3" y="3"></rect>
  <line class="ql-stroke" x1="3" x2="15" y1="9" y2="9"></line>
  <line class="ql-stroke" x1="9" x2="9" y1="3" y2="15"></line>
</svg>
`;

// Register QuillBetterTable
Quill.register({
  'modules/better-table': QuillBetterTable
}, true);

const modules = {
  toolbar: {
    container: [
      [{ 'header': [1, 2, 3, 4, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['table'],
      ['clean'],
      ['clean-format'],
      ['clean-styles']
    ]
  },
  'better-table': {
    operationMenu: {
      items: {
        unmergeCells: {
          text: 'Another unmerge cells name'
        }
      }
    }
  }
};

const Editor = ({
  content,
  url,
  isParsing,
  error,
  onContentChange,
  onUrlChange,
  onParseContent,
  isEnabled,
  isInitialized = false,
  setQuillInstance
}: EditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const isInitializedRef = useRef(false);
  const contentSetRef = useRef(false);
  const [editorKey, setEditorKey] = useState(0);
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [htmlSource, setHtmlSource] = useState('');

  useEffect(() => {
    if (editorRef.current && !isInitializedRef.current) {
      const existingToolbar = document.querySelector('.ql-toolbar');
      if (existingToolbar) {
        existingToolbar.remove();
      }

      // Add custom icons
      const icons = Quill.import('ui/icons');
      icons['clean-format'] = cleanIcon;
      icons['table'] = tableIcon;
      icons['clean-styles'] = cleanStylesIcon;

      // Create a new Quill instance with proper initialization
      const quill = new Quill(editorRef.current, {
        theme: 'snow',
        modules,
        placeholder: 'Start writing or paste your content...',
        readOnly: !isEnabled || showHtmlSource
      });

      // Add handler for custom clean format button
      const toolbar = quill.getModule('toolbar') as { addHandler: (event: string, callback: () => void) => void };
      if (toolbar && typeof toolbar.addHandler === 'function') {
        toolbar.addHandler('clean-format', () => {
          const currentContent = quill.root.innerHTML;
          const cleanedContent = cleanText(currentContent);
          quill.root.innerHTML = cleanedContent;
          onContentChange(cleanedContent);
        });

        // Add handler for clean styles button
        toolbar.addHandler('clean-styles', () => {
          const currentContent = quill.root.innerHTML;
          const cleanedContent = cleanStyles(currentContent);
          quill.root.innerHTML = cleanedContent;
          onContentChange(cleanedContent);
        });

        // Add table handling using better-table
        toolbar.addHandler('table', () => {
          const tableModule = quill.getModule('better-table') as { insertTable: (rows: number, cols: number) => void };
          if (tableModule && typeof tableModule.insertTable === 'function') {
            tableModule.insertTable(3, 3);
          }
        });
      }

      // Store the instance
      quillRef.current = quill;

      // Share the instance with parent
      if (setQuillInstance) {
        setQuillInstance(quill);
      }

      // Add change handler
      quill.on('text-change', () => {
        if (quill && isEnabled && !showHtmlSource) {
          const newContent = quill.root.innerHTML;
          onContentChange(newContent);
        }
      });

      isInitializedRef.current = true;

      // Set initial content if available
      if (content && isInitialized) {
        quill.root.innerHTML = content;
        contentSetRef.current = true;
      }
    }

    return () => {
      if (quillRef.current) {
        quillRef.current.off('text-change');
      }
    };
  }, [isEnabled, onContentChange, content, isInitialized, editorKey, setQuillInstance, showHtmlSource]);

  // Update content when prop changes
  useEffect(() => {
    if (quillRef.current && content && isInitializedRef.current && !showHtmlSource) {
      const currentContent = quillRef.current.root.innerHTML;
      if (currentContent !== content) {
        quillRef.current.root.innerHTML = content;
        contentSetRef.current = true;
      }
    }
  }, [content, showHtmlSource]);

  // Update enabled state
  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(isEnabled && !showHtmlSource);
    }
  }, [isEnabled, showHtmlSource]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onParseContent();
  };

  const handleHtmlSourceChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setHtmlSource(e.target.value);
  };

  const handleApplyHtmlSource = () => {
    // Save the HTML content
    const newHtml = htmlSource;
    
    // Reset the editor
    handleReloadEditor();
    
    // Set the flag to false so we can reinitialize
    setShowHtmlSource(false);
    
    // Update the content after a short delay to ensure the editor is reinitialized
    setTimeout(() => {
      if (quillRef.current) {
        quillRef.current.root.innerHTML = newHtml;
        onContentChange(newHtml);
      } else {
        // If quill isn't ready yet, store the content to be set when initialized
        onContentChange(newHtml);
      }
    }, 50);
  };

  const handleReloadEditor = () => {
    if (quillRef.current) {
      quillRef.current.off('text-change');
      quillRef.current = null;
    }
    isInitializedRef.current = false;
    contentSetRef.current = false;
    setEditorKey(prev => prev + 1);
  };

  const toggleHtmlSource = () => {
    if (showHtmlSource) {
      handleApplyHtmlSource();
    } else {
      setHtmlSource(quillRef.current?.root.innerHTML || '');
      setShowHtmlSource(true);
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-50">
      <div className="bg-white border-b border-gray-200 p-2">
        <form onSubmit={handleSubmit} className="flex items-center space-x-4 mb-2">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <input
                type="url"
                value={url}
                onChange={(e) => onUrlChange(e.target.value)}
                placeholder="Enter URL to parse content"
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button
                type="submit"
                disabled={isParsing || !url.trim()}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {isParsing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  'Parse Content'
                )}
              </button>
              <button
                type="button"
                onClick={handleReloadEditor}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                title="Reload editor"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={toggleHtmlSource}
                className={`inline-flex items-center px-3 py-1.5 border ${showHtmlSource ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-white'} rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                title="View HTML source"
              >
                <Code className="h-4 w-4" />
              </button>
            </div>
            {error && (
              <div className="mt-2 flex items-center text-sm text-red-600">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {error}
              </div>
            )}
          </div>
        </form>
      </div>

      <div className="flex-1 p-6 overflow-auto">
        <div className="max-w-4xl mx-auto bg-white shadow-sm rounded-lg">
          {showHtmlSource ? (
            <div className="flex flex-col h-full">
              <textarea
                value={htmlSource}
                onChange={handleHtmlSourceChange}
                className="w-full h-[calc(100vh-16rem)] p-4 font-mono text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                spellCheck={false}
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleApplyHtmlSource}
                  className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
                >
                  Apply HTML
                </button>
              </div>
            </div>
          ) : (
            <div className="editor-container">
              <div 
                key={editorKey} 
                ref={editorRef} 
                className={`${!isEnabled ? 'opacity-50' : ''}`}
                id="editor-content"
                data-editor-container
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Editor;